/* eslint-disable indent */
import React, { useState, useEffect } from "react";
import { Dropdown } from "react-bootstrap";
import jwtDecode from "jwt-decode";
import {
  format,
  formatDistanceToNow,
  addHours,
  parseISO,
  isValid,
  addDays,
  isBefore,
} from "date-fns";
import { utcToZonedTime } from "date-fns-tz";
import * as Browser from "detect-browser";
import * as moment from "moment-timezone";
import debounce from "lodash/debounce";
import { Map } from "immutable";
import * as configcat from "configcat-js";
import Cookies from "js-cookie";
import FontAwesome from "react-fontawesome";
import liveInterviewIcon from "resources/images/live-interview-fontawesome-icon-dark.svg";
import {
  configCatAppKey,
  odaUrl,
  websiteUrl,
  instancePrefix,
  teamsUrl,
} from "utils/env";
import {
  aptitudeTestIdList,
  personalityTestIdList,
  emotionalTestIdList,
  skillTestIdList,
  riskTestIdList,
} from "containers/App/constants";
import { setLogutFlag } from "containers/App/actions";
import { ChartJsLabelPlugin } from "components/CandidateProfileComponent/ChartJsLabelPlugin";
import {
  traitAbbrevToTestFieldObj,
  traitIdToTest,
  proficiencyToScore,
  CUSTOM_FILTER_OPTIONS_LIBRARY,
  EXTRA_CUSTOM_FILTER_OPTIONS_LIBRARY,
  phoneCountryCodes,
  clptExpressSubTestIds,
  screenReaderCompatibleTests,
  ctrseTranslatedSubTestIds,
} from "./helperConstants";
import { store } from "../app";

export const traitAbbrevToTestField = (traitAbbrev, testId, isTestMaker) => {
  if (isTestMaker === 1 || isTestMaker === "1") {
    return "rawScore";
  }
  if (
    traitAbbrevToTestFieldObj[testId] &&
    traitAbbrevToTestFieldObj[testId][traitAbbrev]
  ) {
    return traitAbbrevToTestFieldObj[testId][traitAbbrev];
  }
  switch (traitAbbrev) {
    case "raw":
      return "rawScore";
    default:
      return traitAbbrev;
  }
};

const wppScoresObject = {
  1: "Low",
  2: "Medium",
  3: "High",
};

export const getWPPScores = (score) => wppScoresObject[score];

const shift = (number, precision, reverseShift) => {
  let precisionMod = precision;
  if (reverseShift) {
    precisionMod = -precision;
  }
  const numArray = `${number}`.split("e");
  return +`${numArray[0]}e${
    numArray[1] ? +numArray[1] + precisionMod : precisionMod
  }`;
};
export const round = (number, precision) =>
  shift(Math.round(shift(number, precision, false)), precision, true);

export const getNewDate = (str) => {
  if (!str || typeof str !== "string") {
    return new Date(str);
  }
  return new Date(str.replace(/-/g, "/"));
};

// eslint-disable-next-line no-script-url
const blacklistedTerms = ["javascript:", "data:"];
export const validateCandidateInput = (input, requiredText, maxLength) => {
  if (
    (requiredText && input.indexOf(requiredText) < 0) ||
    (maxLength && input.length > maxLength)
  ) {
    return false;
  }
  if (
    blacklistedTerms.reduce(
      (bool, blacklistedTerm) => bool || input.indexOf(blacklistedTerm) > -1,
      false
    )
  ) {
    return false;
  }
  return true;
};
const charactersToEncode = [
  "'",
  '"',
  "`",
  "{",
  "}",
  "[",
  "]",
  "<",
  ">",
  "/",
  "\\",
  "=",
  "&",
];
export const encodeDangerousCharacters = (stringToEval) =>
  stringToEval
    .split("")
    .reduce(
      (encodedString, char) =>
        `${encodedString}${
          charactersToEncode.includes(char) ? encodeURIComponent(char) : char
        }`,
      ""
    );

export const testIdToTestName = {
  10: "Criteria Cognitive Aptitude Test",
  12: "Criteria Basic Skills Test",
  13: "Criteria Personality Inventory",
  14: "MiniCog Rapid Assessment Battery",
  15: "Customer Service Aptitude Profile",
  16: "Wiesen Test of Mechanical Aptitude",
  17: "Typing Test",
  18: "Ten Key Test",
  19: "Microsoft Excel 2003",
  20: "Microsoft Word 2003",
  21: "Microsoft Excel 2007",
  22: "Microsoft Word 2007",
  23: "Criteria Attention Skills Test",
  24: "Sales Achievement Predictor",
  25: "Working Memory",
  26: "Selective Attention",
  27: "Mental Rotation",
  28: "Divided Attention",
  29: "Employee Personality Profile",
  30: "Computer Literacy and Internet Knowledge Test",
  31: "Workplace Productivity Profile",
  32: "Microsoft Excel 2010",
  33: "Microsoft Word 2010",
  34: "Employee Personality Profile",
  35: "Microsoft Excel 2013",
  36: "Microsoft Word 2013",
  37: "Universal Cognitive Aptitude Test",
  38: "Microsoft PowerPoint 2013",
  39: "Microsoft Excel 2016",
  40: "Microsoft Word 2016",
  41: "Microsoft PowerPoint 2016",
  42: "General Aptitude Mobile Evaluation",
  43: "Criteria Mechanical Reasoning Assessment",
  44: "Emotify",
  45: "Cognify",
  46: "Workplace Safety Profile",
  47: "Criteria Cognitive Aptitude Test (CCAT cAT)",
  48: "Illustrait",
  49: "Computer Literacy and Internet Knowledge Test",
  50: "Workplace Alignment Assessment",
  51: "Emotify v2",
  52: "UCognify",
  53: "Excel 365",
  54: "Word 365",
  55: "PowerPoint 365",
  57: "Criteria Language Proficiency Test - English",
  // LAST LINE - DO NOT MOVE THIS COMMENT traitIdToTest
};

/* ---------- React-Table Sorting Functions ----------- */
export const sortCreatedBy = (a, b) => {
  if (a.get("createdBy") === b.get("createdBy")) {
    return a.get("jobTitle").localeCompare(b.get("jobTitle"));
  }
  if (a.get("createdBy") === 0) {
    return 1;
  }
  if (b.get("createdBy") === 0) {
    return -1;
  }

  if (a.get("firstName") === b.get("firstName")) {
    return a.get("lastName").localeCompare(b.get("lastName"));
  }

  return a.get("firstName").localeCompare(b.get("firstName"));
};

export const dateSorter = (a, b, order) => {
  if (order === "desc") {
    if (format(a, "yyyy-MM-dd") > format(b, "yyyy-MM-dd")) {
      return -1;
    }
    if (format(a, "yyyy-MM-dd") < format(b, "yyyy-MM-dd")) {
      return 1;
    }
    return 0;
  }
  if (format(a, "yyyy-MM-dd") < format(b, "yyyy-MM-dd")) {
    return -1;
  }
  if (format(a, "yyyy-MM-dd") > format(b, "yyyy-MM-dd")) {
    return 1;
  }
  return 0;
};

export const getAssociatedJobsList = (
  associatedJobsList,
  associatedJobsLoaded
) => {
  if (associatedJobsList.size > 0) {
    return associatedJobsList;
  }
  if (associatedJobsLoaded) {
    return (
      <span className="text-center pt-3 pb-3">
        There are no jobs to display
      </span>
    );
  }
  return null;
};

export const getCookieValue = (cookie, key) => {
  const re = new RegExp(`/(?:(?:^|.*;\\s*)${key}\\s*\\=\\s*([^;]*).*$)|^.*$/`);
  return cookie.replace(re, "$1");
};

export const useWindowSize = () => {
  const [windowSize, setWindowSize] = useState({
    width: undefined,
    height: undefined,
  });

  useEffect(() => {
    // Handler to call on window resize
    function handleResize() {
      // Set window width/height to state
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    }

    // Create the debounced function outside so we can reference it in the cleanup
    const debouncedHandleResize = debounce(handleResize, 150);

    // Add event listener
    window.addEventListener("resize", debouncedHandleResize);

    // Call handler right away so state gets updated with initial window size
    handleResize();

    // Remove event listener on cleanup
    return () => {
      window.removeEventListener("resize", debouncedHandleResize);
    };
  }, []); // Empty array ensures that effect is only run on mount

  return windowSize;
};

export const getConfigCatObject = async (userData) => {
  // in case the account is either an AU or US account, but has the `locale` value set to the
  // opposite, we need to determine which account instance prefix to use based on the subdomain
  // since both AU and US company accounts can have the same id some circumstances

  const subdomainLocaleInstancePrefix =
    getSubDomain() === "app-au" ? "AU" : "US";

  const hsVersionName = userData && getHSVersionName(userData.hsVersion);
  const statusName =
    userData.status === "free_trial" ? "free trial" : userData.status;

  const userObject = {
    identifier: userData.userAccountId,
    custom: {
      companyAccountId: userData.companyAccountId.toString(),
      partnerAccountId: userData.partnerAccountId
        ? userData.partnerAccountId.toString()
        : null,
      locale: userData.locale,
      companyAccountIdWithLocale: `${subdomainLocaleInstancePrefix}${userData.companyAccountId}`,
      planType: hsVersionName,
      status: statusName,
    },
  };

  const logger = configcat.createConsoleLogger(configcat.LogLevel.Error);

  const configCatClient = configcat.getClient(
    configCatAppKey,
    configcat.PollingMode.LazyLoad,
    {
      logger,
    }
  );

  const [
    isAnalyticsEnabled,
    isTeamsEnabled,
    isAdjustedTimeMultiplierEnabled,
    isAISupportAssistantEnabled,
    isNewAnalyticsEnabled,
    showManagerGuideTab,
    dsrCompanyHasSiOnly,
    dsrCompanyHasDevelopOnly,
    dsrCompanyHasSiAndDevelop,
    dsrCompanyHasNone,
    ftWelcomeBannerCtaButtonText,
    isCompareCandidatesToPostHireTeamsEnabled,
    isLandingPageBuilderEnabled,
    ftDashboardExperimentAllocation,
  ] = await Promise.all([
    configCatClient.getValueAsync("isAnalyticsEnabled", false, userObject),
    configCatClient.getValueAsync("isTeamsEnabled", false, userObject),
    configCatClient.getValueAsync(
      "isadjustedtimemultiplierenabled",
      false,
      userObject
    ),
    configCatClient.getValueAsync(
      "isaisupportassistantenabled",
      false,
      userObject
    ),
    configCatClient.getValueAsync("isnewanalyticsenabled", false, userObject),
    configCatClient.getValueAsync("showmanagerguidetab", false, userObject),
    configCatClient.getValueAsync(
      "dsrCompanyHasSiOnly",
      "dsr-si-adopt-1",
      userObject
    ),
    configCatClient.getValueAsync(
      "dsrCompanyHasDevelopOnly",
      "dsr-si-upgrade-1",
      userObject
    ),
    configCatClient.getValueAsync(
      "dsrCompanyHasSiAndDevelop",
      "dsr-si-adopt-1",
      userObject
    ),
    configCatClient.getValueAsync(
      "dsrCompanyHasNone",
      "dsr-si-upgrade-1",
      userObject
    ),
    configCatClient.getValueAsync(
      "ftWelcomeBannerCtaButtonText",
      "Try Our Tests",
      userObject
    ),
    configCatClient.getValueAsync(
      "iscomparecandidatestoposthireteamsenabled",
      false,
      userObject
    ),
    configCatClient.getValueAsync(
      "islandingpagebuilderenabled",
      false,
      userObject
    ),
    configCatClient.getValueAsync(
      "ftDashboardExperimentAllocation",
      "standard welcome banner",
      userObject
    ),
  ]);

  const configCatObject = {
    isAnalyticsEnabled,
    isTeamsEnabled,
    isAdjustedTimeMultiplierEnabled,
    isAISupportAssistantEnabled,
    isNewAnalyticsEnabled:
      process.env.NODE_ENV === "development" ? true : isNewAnalyticsEnabled, // TODO: remove the env check when new analytics is ready to be released
    showManagerGuideTab,
    dsrCompanyHasSiOnly,
    dsrCompanyHasDevelopOnly,
    dsrCompanyHasSiAndDevelop,
    dsrCompanyHasNone,
    ftWelcomeBannerCtaButtonText,
    isCompareCandidatesToPostHireTeamsEnabled,
    isLandingPageBuilderEnabled,
    ftDashboardExperimentAllocation,
  };
  return configCatObject;
};

// since there is NA database time (PT) and AU database time (AEST), we have to account for edge cases where a user's locale does not match their
// platform instance by converting dates to their proper timezone.
export const convertDateTimezone = (date, isAU) => {
  const hostnameArray = window.location.hostname.split(".");
  const subdomain = hostnameArray[0];

  // date gets passed in as a string, for example '2022-04-11 10:45:00'
  // first, we have to convert the date based on the database region (the first tz method)
  // then, we need to convert the converted date again based on the company's selected locale (the second tz method)
  const convertedDate = moment
    .tz(
      date,
      subdomain === "app-au" ? "Australia/Sydney" : "America/Los_Angeles"
    )
    .tz(isAU ? "Australia/Sydney" : "America/Los_Angeles");

  // format the resulting moment object as the original string format and use parseISO from date-fns so that we can use the return value
  // for other date-fns functions
  return parseISO(moment(convertedDate).format("YYYY-MM-DD HH:mm:ss"));
};

export const getHoursToAdd = (eventDate, isAU) => {
  // eventDate will be a Date object generated from the local timezone
  // we can use moment to detect if the date falls in the DST period and then use the company locale to calculate the time difference (hours to add) accurately.
  // Note: this is conditioned on the locale and not the NA or AU DB instance because that should already be accounted for in convertDateTimezone()

  const isTestDateDaylightSavingsObserved = () => moment(eventDate).isDST();

  if (isAU) {
    return (
      (isTestDateDaylightSavingsObserved() ? -11 : -10) -
      new Date().getTimezoneOffset() / 60 // user's  current location timezone offset (also takes in consideration of daylight savings time)
    );
  }

  return (
    (isTestDateDaylightSavingsObserved() ? 7 : 8) -
    new Date().getTimezoneOffset() / 60 // user's current location timezone offset (also takes in consideration of daylight savings time)
  );
};

export const getMostRecentCandidateTestEvent = (candidate, tab, isAU) => {
  const dateField =
    // eslint-disable-next-line no-nested-ternary
    tab === "Scheduled"
      ? "dateEventAdded"
      : tab === "Complete"
        ? "dateEventTaken"
        : "dateEventAdded";
  return candidate
    .get("testEvents")
    .valueSeq()
    .reduce((mostRecent, testEvent) => {
      // we need to convert the latest testEvent from the DB to the proper timezone (PT or AEST) based on both company locale and instance
      // to properly make comparisons with Date objects generated using local timezones
      const latestTestEvent = convertDateTimezone(
        testEvent.get(dateField),
        isAU
      );

      if (getNewDate(latestTestEvent) > mostRecent) {
        return getNewDate(latestTestEvent);
      }

      return mostRecent;
    }, getNewDate("2000-01-01 01:01:01"));
};

export const getAppliedOnTimeStamp = (candidate, tab, isAU) => {
  const eventDate = getMostRecentCandidateTestEvent(candidate, tab, isAU);

  const hoursToAdd = getHoursToAdd(eventDate, isAU);

  return `${formatDistanceToNow(addHours(eventDate, hoursToAdd))} ago`;
};

/*
 *  Purpose:  To determine which default email template to choose based. use job-level setting if set, otherwise use company-level setting.
 *  Parameters:
 *    companyTemplateId:  null | 0 | int
 *    jobTemplateId: null | 0 | int
 *    vhs
 *  Returns: an integer of the email template ID
 *  Note: remember to parse return to String if using as an select input value
 */
export const getDefaultEmailTemplateId = (
  companyTemplateId = 0,
  jobTemplateId = null,
  vhs = 1
) => {
  if (vhs >= 2) {
    if (jobTemplateId !== null) {
      return jobTemplateId;
    }
    if (
      companyTemplateId === null ||
      typeof companyTemplateId === "undefined"
    ) {
      return 0;
    }
    return companyTemplateId;
  }
  return 0;
};

// Check if a certain trait meets what's been set in the score range requirement
const ifTestTraitPassesRange = (test, testId, range) => {
  let testTraitPassesRange = null;
  const score =
    range.has("traitAbbrev") && range.get("traitAbbrev")
      ? test.get(traitAbbrevToTestFieldObj[testId][range.get("traitAbbrev")])
      : test.get(
          traitAbbrevToTestFieldObj[testId] && range.get("traitId")
            ? traitAbbrevToTestFieldObj[testId][
                traitIdToTest[range.get("traitId")].traitAbbrev
              ]
            : "rawScore"
        );
  const isDefaultRange = range.get("isDefault") || range.get("systemDefault");
  if (
    typeof score === "number" &&
    (`${test.get("testId")}` === String(testId) ||
      `TM${test.get("testId")}` === String(testId))
  ) {
    if (
      score < Number(range.get("min_scoreRange")) ||
      (score > Number(range.get("max_scoreRange")) && !isDefaultRange)
    ) {
      testTraitPassesRange = false;
    } else {
      testTraitPassesRange = true;
    }
  } else if (range.get("rangeName") && range.get("rangeName") === "Ranking") {
    if (
      proficiencyToScore[score] &&
      (proficiencyToScore[score] < Number(range.get("min_scoreRange")) ||
        proficiencyToScore[score] > Number(range.get("max_scoreRange")))
    ) {
      testTraitPassesRange = false;
    } else {
      testTraitPassesRange = true;
    }
  }
  return testTraitPassesRange;
};

export const determineCandidatePassesTestRange = (
  testRanges,
  testId,
  testEvent
) => {
  let passesRange = null; // null = range test not taken by candidate, false = range test taken and failed, true = range test taken and passed
  if (testEvent && testRanges) {
    const test = testEvent
      .get("tests")
      .find((testTaken) => testTaken.get("testId") === testId);
    testRanges.forEach((range) => {
      if (passesRange === false) {
        return;
      }
      passesRange = ifTestTraitPassesRange(test, testId, range);
    });
    return passesRange;
  }
};

export const showScoreRangesMetIcon = (
  allScoreRangesMetObject,
  filteredScoreRangeArray
) => {
  let numberOfPassedTests = 0;
  Object.keys(allScoreRangesMetObject).map((testId) => {
    if (allScoreRangesMetObject[testId]) {
      numberOfPassedTests += 1;
    }
  });
  return (
    filteredScoreRangeArray.length !== 0 &&
    filteredScoreRangeArray.length === numberOfPassedTests
  );
};

// Determines whether a test's results are considered invalid in order to display the invalid test warning icon.
// Note 'invalid' is the most common invalidity indicator for assessment results, but exceptionally the CPI returns a
// 'cpiInvalidity' value and Illustrait returns an 'inconsistencyPercentile' value.
export const isInvalidTest = (test) =>
  Boolean(
    (test.get("invalid") && test.get("invalid") === 1) ||
      (test.get("cpiInvalidity") && test.get("cpiInvalidity") === 1) ||
      (test.get("inconsistencyPercentile") &&
        test.get("inconsistencyPercentile") >= 98)
  );

/*
 *  Purpose: Checks localStorage for 'token' and checks if it's expired.
 *  Parameters: No parameters.
 *  Returns: Returns true if there is no token in localStorage OR the expiration has passed.
 */
export const isTokenExpired = () => {
  const token = localStorage.getItem("token")
    ? jwtDecode(localStorage.getItem("token"))
    : null;
  return !token || token.exp < Math.floor(Date.now() / 1000);
};

/*
 *  Purpose: To clear localStorage with the exception of 'partnersToken' and removes 'critToken' cookie
 *  Parameters: none
 *  Returns: none
 */
export const logUserOut = async ({ forceLogout } = { forceLogout: false }) => {
  // This keeps the partnersToken logged in logged out of session - related to PRO-6022
  const partnersToken = localStorage.getItem("partnersToken");
  localStorage.clear();
  if (partnersToken) {
    localStorage.setItem("partnersToken", partnersToken);
  }

  // Remove critToken cookie
  Cookies.remove("critToken", {
    path: "/",
    domain:
      new URL(teamsUrl).hostname === "localhost"
        ? "localhost"
        : ".criteriacorp.com",
  });

  // If the user has been forcefully logged out we should set this flag so that the user can see the security message.
  if (forceLogout) {
    store.dispatch(setLogutFlag());
  } else {
    window.location.href = "/";
  }
};

// Helps properly decode base 64 - solves the "Unicode Problem"
// Resource: https://stackoverflow.com/questions/30106476/using-javascripts-atob-to-decode-base64-doesnt-properly-decode-utf-8-strings
export const decodeBase64 = (base64) => {
  const decoder = new TextDecoder();
  const text = atob(base64);
  const { length } = text;
  const bytes = new Uint8Array(length);
  for (let i = 0; i < length; i += 1) {
    bytes[i] = text.charCodeAt(i);
  }

  return decoder.decode(bytes);
};

export const getSubDomain = () => {
  const hostnameArray = window.location.hostname.split(".");
  const subdomain = hostnameArray[0];

  return subdomain;
};

export const getFormattedTimeZoneText = (
  timeZoneCode,
  timeZoneList,
  isAU,
  companyDefaultTimezone,
  isLabelValue = true
) => {
  let defaultTimezone = timeZoneCode;

  if (!timeZoneCode) {
    defaultTimezone = isAU ? "Australia/Sydney" : "America/Los_Angeles";
  }

  const selectedTimeZone = timeZoneList.filter(
    (timeZone) => timeZone.tzCode === defaultTimezone
  );

  const labelText = selectedTimeZone[0].label;
  let parsedTZCode = defaultTimezone.replace(/_/g, " ");

  /*
   * Asia/Shanghai and Asia/Beijing have the same time zone, however Asia/Shanghai is the IANA code
   * to be used for timezone libraries/databases. We want the UI in the dropdown formatted text to show
   * Asia/Beijing to be more accurate for the Mainland China timezone (China Standard Time).
   */
  if (timeZoneCode === "Asia/Shanghai") {
    parsedTZCode = "Asia/Beijing";
  }

  const addTimeText = labelText.includes("Time") ? "" : " Time";
  const showDefaultTimeZoneText =
    (companyDefaultTimezone && companyDefaultTimezone === defaultTimezone) ||
    (!companyDefaultTimezone &&
      isAU &&
      defaultTimezone === "Australia/Sydney") ||
    (!companyDefaultTimezone &&
      !isAU &&
      defaultTimezone === "America/Los_Angeles");

  return `${labelText}${addTimeText} (${parsedTZCode})${
    isLabelValue && showDefaultTimeZoneText ? " - Default" : ""
  }`;
};

/*
 *  Purpose:  Takes in a candidate object and looks through its testevents and returns the videoInterviewData and ID, from a specific stage.
 *  Parameters:
 *    candidateInfo:  candidateObj;
 *  Returns: An object candidateVideoInterviewData that can be deconstructed to take the following variables:
 *    {
 *        candidateVideoInterviewDataToShowID,  -- The id of the videoInterview
 *        candidateVideoInterviewDataToShow,    -- The actual Video Interview Data
 *        testEventData,                        -- Test Event object holding the video interview object
 *    }
 *  Notes: Document on new Candidate Video Interview data structure https://criteria.atlassian.net/wiki/spaces/FRO/pages/1573978120/Video+Interviews+Data+Model+Switchover
 */

export const getCandidateVideoInterviewData = (candidateInfo) => {
  const candidateVideoInterviewData = {
    candidateVideoInterviewDataToShowID: null,
    candidateVideoInterviewDataToShow: null,
    testEventData: null,
  };
  const candidateEvents = candidateInfo
    ? candidateInfo.get("testEvents")
    : new Map();

  if (candidateEvents.get("videoInterview")) {
    // we stop and return all necessary values.
    return {
      testEventData: candidateEvents,
      candidateVideoInterviewDataToShow: candidateEvents.get("videoInterview"),
      candidateVideoInterviewDataToShowID: String(
        candidateEvents.get("videoInterview").get("videoInterviewId")
      ),
    };
  }

  let videoInterviews = null;
  // Looks through all the events and pulls out the videoInterviews if any.
  candidateEvents.forEach((testEvent) => {
    if (!videoInterviews && testEvent.get("videoInterviews")) {
      // Got to pull the eventExpirationDate(previously deadline), scheduledBy and dateEventAdded(previously dateCreated)
      candidateVideoInterviewData.testEventData = testEvent;
      videoInterviews = testEvent.get("videoInterviews");
    }
  });

  if (videoInterviews) {
    // If any of the videoInterviews are not outdated then return that one, else return a outdated one.
    if (
      videoInterviews.some((videoInterview) => !videoInterview.get("outdated"))
    ) {
      const [viID, viData] = videoInterviews
        .filter((videoInterview) => !videoInterview.get("outdated"))
        .entrySeq()
        .first();
      candidateVideoInterviewData.candidateVideoInterviewDataToShow = viData;
      candidateVideoInterviewData.candidateVideoInterviewDataToShowID = viID;
    } else {
      const [viID, viData] = videoInterviews.entrySeq().first();
      candidateVideoInterviewData.candidateVideoInterviewDataToShow = viData;
      candidateVideoInterviewData.candidateVideoInterviewDataToShowID = viID;
    }
  }
  return candidateVideoInterviewData;
};

export const getCandidateLiveInterviewData = (
  candidateInfo,
  deletedTestEventIds = []
) => {
  const candidateLiveInterviewData = {}; // object of objects

  const candidateEvents = candidateInfo
    ? candidateInfo.get("testEvents")
    : new Map();

  // for v2 data structure
  if (candidateEvents.get("liveInterviews")) {
    // we stop and return all necessary values.
    candidateEvents.get("liveInterviews").forEach((liveInterview) => {
      if (
        deletedTestEventIds.includes(String(liveInterview.get("testEventId")))
      ) {
        return;
      }
      const liveInterviewId = String(liveInterview.get("liveInterviewId"));
      candidateLiveInterviewData[liveInterviewId] = {
        liveInterviewId,
        liveInterviewData: liveInterview,
        testEventData: candidateEvents,
      };
    });

    return candidateLiveInterviewData;
  }

  // for v1 data structure
  // Looks through all the events and pulls out the live interviews if any.
  candidateEvents.forEach((testEvent) => {
    let liveInterviews = null;

    if (!liveInterviews && testEvent.get("liveInterviews")) {
      liveInterviews = testEvent.get("liveInterviews");
    }

    if (liveInterviews) {
      liveInterviews.forEach((liveInterview) => {
        if (
          deletedTestEventIds.includes(String(liveInterview.get("testEventId")))
        ) {
          return;
        }
        const liveInterviewId = String(liveInterview.get("liveInterviewId"));
        candidateLiveInterviewData[liveInterviewId] = {
          liveInterviewId,
          liveInterviewData: liveInterview,
          testEventData: candidateEvents,
        };
      });
    }
  });

  return candidateLiveInterviewData;
};

// This function is responsible for taking in a eventId and copying the URL of the event onto the clipboard.
export const copyEventUrlToClipboard = (odaLink) => {
  const textField = document.createElement("textarea");
  textField.innerText = odaLink;
  document.body.appendChild(textField);
  textField.select();
  document.execCommand("copy");
  textField.remove();
};

// Determines if the critToken cookie is valid, i.e. comes from the same instance/issuer
// in case the user has previously logged into another Criteria instance in another tab/window.
// If it is not valid, we should not consider the user authenticated for this instance.
// Note: critTokenIssuer will be the websiteUrl for whichever instance it was issued from
// ex. 'https://hireselect.criteriacorp.com'
export const isValidCritToken = (critToken) => {
  if (critToken) {
    const critTokenIssuer = jwtDecode(critToken).iss;
    const subdomain = getSubDomain();

    // if the subdomain is localhost, the available critToken can only be a cookie set when
    // teaming is running in localhost, so we can make an exception here
    if (subdomain === "localhost" || websiteUrl === `${critTokenIssuer}/`) {
      return critToken;
    }
  }

  return false;
};

// Returns endpoint URL based on domain for serverless integration requests
export const getServerlessIntegrationsDomain = () => {
  let domainSuffix = "";

  if (instancePrefix === "AU_") {
    domainSuffix = "-au";
  } else if (process.env.NODE_ENV === "staging") {
    domainSuffix = "-stg";
  } else if (process.env.NODE_ENV === "development") {
    domainSuffix = "-dev";
  }

  return `https://integrations${domainSuffix}.criteriacorp.com/api/v1/`;
};

// returns the Alcami domain to be used in the iframe URL
export const getAlcamiDomain = () => {
  if (instancePrefix === "AU_") {
    return "api.alcami.video";
  }

  if (process.env.NODE_ENV === "production") {
    return "api.alcami-cc.video";
  }

  return "api.alcami-cc-staging.video";
};

export const getVIDomain = () => {
  if (instancePrefix === "AU_") {
    return "interviewing-au.criteriacorp.com";
  }

  if (process.env.NODE_ENV === "production") {
    return "interviewing.criteriacorp.com";
  }

  if (process.env.NODE_ENV === "staging") {
    return "interviewing-stg.criteriacorp.com";
  }

  return "interviewing-dev.criteriacorp.com";
};

// returns the Alcami env to be used in the iframe URL
export const getAlcamiEnv = () =>
  process.env.NODE_ENV === "development" ? "dev" : process.env.NODE_ENV;

export const getVIIframeUrl = (
  candidateInfo,
  candidateVideoInterviewDataToShow,
  videoInterviewIframeViewStatus
) => {
  const viDomain = getVIDomain();
  const videoInterviewObject =
    candidateInfo
      ?.get("testEvents")
      ?.get(String(candidateVideoInterviewDataToShow?.get("testEventId"))) ||
    candidateInfo?.get("testEvents")?.get("videoInterview");

  const eventId = videoInterviewObject?.get("eventId");

  let pathValue = "";
  if (videoInterviewIframeViewStatus === "reviewing") {
    pathValue += "responses";
  } else {
    pathValue += "evaluate";
  }

  return `https://${viDomain}/async/iframe/load/events/${eventId}/${pathValue}`;
};

// takes in jobData and determines if the job has Illustrait or CLPT in its test batteries AND equivalent assessmentSettings values
export const determineIfJobHasAssessmentSettings = (jobData) => {
  let hasIllustraitSettings = false;
  let hasClptSettings = false;

  if (
    jobData &&
    jobData.size > 0 &&
    jobData.getIn(["batteries", "data"]) &&
    jobData.getIn(["batteries", "data"]).size
  ) {
    const illustraitAssessmentSettings = jobData.getIn([
      "assessmentSettings",
      "48",
    ]);
    const clptAssessmentSettings = jobData.getIn(["assessmentSettings", "57"]);

    jobData
      .getIn(["batteries", "data"])
      .valueSeq()
      .forEach((testBattery) => {
        if (illustraitAssessmentSettings && testBattery.get("48")) {
          hasIllustraitSettings = true;
        }
        if (clptAssessmentSettings && testBattery.get("57")) {
          hasClptSettings = true;
        }
      });
  }

  return [hasIllustraitSettings, hasClptSettings];
};

/* if existingDateTo and existingDateFrom are passed in when editing an existing form, they are
 * passed in as "yyyy-MM-dd" strings. however, using the Date constructor on on "yyyy-MM-dd" strings
 * can result in undesired timezone adjustment that could output "2022-03-24" as "..Mar 23 2022..".
 * converting the "yyyy-MM-dd" string to "MM/dd/yyyy" before using Date avoids this behavior. Safari
 * and Firefox browsers read `-` as invalid dates so we use '/' as a replacement to fix the Invalid Date
 * issues. See both https://stackoverflow.com/questions/16616950/date-function-returning-invalid-date-in-safari-and-firefox
 * and https://stackoverflow.com/questions/7556591/is-the-javascript-date-object-always-one-day-off.
 */
export const accountForExistingTimeOffset = (existingDate) =>
  new Date(format(parseISO(existingDate), "MM/dd/yyyy"));

export const formatDateToTime = (date) => format(date, "h:mm a");

export const formattedDateAndTime = (date, time, showAMPM) => {
  const dateFormat = showAMPM ? "h:mm a" : "HH:mm";
  const formattedTime = isValid(time) ? format(time, dateFormat) : null;

  if (date && formattedTime) {
    return `${date} ${formattedTime}`;
  }

  return null;
};

export const getEventExpirationHelpDocUrl = (isAU) => {
  // default url to NA help docs
  let url =
    "https://criteria.helpdocs.io/article/cmybdsb1l5-setting-an-expiration-date-for-candidate-invitations";

  if (isAU) {
    url =
      "https://criteriacorp.helpdocs.io/article/mz7w19vkz3-setting-an-expiration-date-for-candidate-invitations";
  }

  return url;
};

/* Format number of seconds into mm:ss format
 * eg: 0 => 00:00 (this is the only difference with fancyTimeFormat)
 *     30 => 00:30
 *     92 => 01:31
 */
export const toMinutesAndSecondsFormat = (input) => {
  const sec_num = parseInt(input, 10); // don't forget the second param
  let minutes = Math.floor(sec_num / 60);
  let seconds = sec_num - minutes * 60;

  if (minutes < 10) {
    minutes = `0${minutes}`;
  }
  if (seconds < 10) {
    seconds = `0${seconds}`;
  }
  return `${minutes}:${seconds}`;
};

/* Format number of seconds into (hh:)mm:ss format
 * eg: 0 => "None" (this is the only difference with fancyTimeFormat)
 *     30 => 00:30
 *     92 => 01:31
 *     3663 => 01:01:03
 */
export const fancyTimeFormat = (time) => {
  if (!time) {
    return "None";
  }

  // Hours, minutes and seconds
  /* eslint no-bitwise: 0 */
  const hrs = ~~(time / 3600);
  const mins = ~~((time % 3600) / 60);
  const secs = ~~time % 60;

  // Output like "01:01" or "04:03:59" or "123:03:59"
  let ret = "";

  if (hrs > 0) {
    ret += `00${hrs}`.slice(Math.min(-2, hrs.toString().length * -1));
    ret += ":";
  }

  ret += `00${mins}`.slice(-2);
  ret += ":";
  ret += `00${secs}`.slice(-2);

  return ret;
};

/* Capitalize the first letter of a string
 * eg: "hello" => "Hello"
 *     "good morning" => "Good morning"
 */
export const capitalize = (name) =>
  name.charAt(0).toUpperCase() + name.slice(1);

/* Generate a prefix followed by a specified length of random numbers
 * eg: generateTemporaryId("file", 4) => "file_8371"
 */
export const generateTemporaryId = (prefix = "temp", length = 6) => {
  const random_number = `${new Date().getTime()}00000${
    Math.random() * 100000
  }`.slice(-length);

  return `${prefix}_${random_number}`;
};

/* Check to see if the browser is Safari
 */
export const isSafari = () => {
  const { userAgent } = navigator;
  /* apparently Chrome (at least on Mac) has 'Safari' on its userAgent string,
   * but since Safari doesn't have 'Chrome' on its userAgent string
   * we can use this to check.
   */
  return userAgent.includes("Safari") && !userAgent.includes("Chrome");
};

/*
 * NOTE: This is a duplicate of a fe-applicants app behaviour, so
 * please keep the two in sync
 */
const browser = Browser.detect();
const isIosOrSafari =
  browser && (["safari", "ios"].includes(browser.name) || browser.os === "iOS");

/**
 * MediaRecorder MIME types to try, in preference order.
 *
 * We assume that if and when iOS does support WebM for MediaRecorder, it's
 * still going to be preferable to ask iOS/Safari for MP4, whereas other
 * browsers will natively support WebM better, even if they add support for MP4
 * in future.
 */
const mimeTypePreferences = isIosOrSafari
  ? [
      "video/mp4",
      "video/webm;codecs=h264,opus",
      "video/webm;codecs=h264",
      "video/webm",
    ]
  : [
      "video/webm;codecs=h264,opus",
      "video/webm;codecs=h264",
      "video/webm",
      "video/x-matroska;codecs=h264,aac",
      "video/x-matroska;codecs=h264,mp3",
      "video/x-matroska;codecs=h264,opus",
      "video/x-matroska;codecs=h264",
      "video/mp4",
    ];

const testSupportsMediaRecorder = (mimeType) => {
  try {
    return window.MediaRecorder.isTypeSupported(mimeType);
  } catch (ex) {
    return false;
  }
};

export const preferredMediaRecorderMimeType = mimeTypePreferences.find(
  testSupportsMediaRecorder
);

export const numberToOrdinalString = (num) => {
  if (num % 10 === 1 && num % 100 !== 11) {
    return `${num}st`;
  }
  if (num % 10 === 2 && num % 100 !== 12) {
    return `${num}nd`;
  }
  if (num % 10 === 3 && num % 100 !== 13) {
    return `${num}rd`;
  }

  return `${num}th`;
};

export const testScoreRangeHasTextScores = (testId, rangeName) =>
  (["15", "24", "31"].includes(String(testId)) && rangeName === "Raw Score") ||
  (["30", "49"].includes(String(testId)) && rangeName === "Ranking") ||
  (["53", "54", "55", "57"].includes(String(testId)) &&
    rangeName.includes("Proficiency"));

export const getTestClassName = (
  otherClasses,
  testId,
  isStructuredInterview
) => {
  let className = otherClasses ? `${otherClasses} ` : "";

  if (isStructuredInterview) {
    className += "video-interview-tag";
  } else {
    const id = Number(testId);

    if (aptitudeTestIdList.includes(id)) {
      className += "aptitude-test-tag";
    } else if (personalityTestIdList.includes(id)) {
      className += "personality-test-tag";
    } else if (emotionalTestIdList.includes(id)) {
      className += "emotional-test-tag";
    } else if (skillTestIdList.includes(id)) {
      className += "skills-test-tag";
    } else if (riskTestIdList.includes(id)) {
      className += "risk-test-tag";
    } else {
      className += "testmaker-test-tag";
    }
  }

  return className;
};

// TODO: add this to the adding testId automation generator file
export const checkIfWorkplaceInsightsTest = (isAU, testId) => {
  if (isAU) {
    return [10, 34, 45, 46, 50, 51, 48].includes(Number(testId));
  }
  return [34, 48].includes(Number(testId));
};

// Returns a boolean whether or not the specific job has a live interview in any stage.
export const getJobHasLiveInterview = (jobData) =>
  jobData && jobData.get("liveInterviews");

// Returns a boolean wether or not the specific job has live interview in the incoming stage.
export const getHasLiveInterviewInIncoming = (jobData) =>
  jobData && jobData.get("liveInterviews")
    ? jobData
        .get("liveInterviews")
        .some(
          (liveInterview) => liveInterview.get("defaultStageIdentifier") === 1
        )
    : false;

// Returns a boolean whether or not the specific job has an active video interview in any stage.
export const getJobHasActiveVideoInterview = (jobData) =>
  jobData &&
  jobData.get("videoInterviews") &&
  jobData
    .get("videoInterviews")
    .some((videoInterview) => videoInterview.get("active"));

export const getPipelineStagesWithTestBatteries = (jobData) => {
  const pipelineStagesWithTestBatteries = [];
  if (jobData && jobData.get("batteries")) {
    jobData.getIn(["batteries", "data"]).forEach((battery) => {
      battery.forEach((testId) => {
        testId.forEach((test) => {
          if (
            !pipelineStagesWithTestBatteries.includes(
              String(test.get("pipelineStageMapId"))
            )
          ) {
            pipelineStagesWithTestBatteries.push(
              String(test.get("pipelineStageMapId"))
            );
          }
        });
      });
    });
  }

  return pipelineStagesWithTestBatteries;
};

export const hasLiveInterviewInStage = (jobData, currentPipelineStageMapId) =>
  jobData &&
  jobData.get("liveInterviews") &&
  jobData
    .get("liveInterviews")
    .some(
      (liveInterview) =>
        liveInterview.get("pipelineStageMapId") === currentPipelineStageMapId
    );

// Returns a boolean whether or not there is at least 1 active video interview in a pipeline stage.
export const hasVideoInterviewInStage = (jobData, pipelineStageMapId) =>
  Boolean(
    jobData &&
      jobData.get("videoInterviews") &&
      jobData
        .get("videoInterviews")
        .some(
          (videoInterview) =>
            videoInterview.get("active") &&
            String(videoInterview.get("pipelineStageMapId")) ===
              String(pipelineStageMapId)
        )
  );

// Returns a boolean whether or not there is at least 1 assessment in a pipeline stage.
export const hasAssessmentsInStage = (jobData, pipelineStageMapId) =>
  Boolean(
    jobData &&
      jobData.size &&
      jobData.getIn(["batteries", "data"]) &&
      jobData
        .getIn(["batteries", "data"])
        .some((testEvent) =>
          testEvent.some(
            (test) =>
              String(test.getIn(["0", "pipelineStageMapId"])) ===
              String(pipelineStageMapId)
          )
        )
  );

// Returns a boolean whether or not there is at least 1 evaluation tool in a given stage.
export const getPipelineStageHasEvaluationTool = (
  jobData,
  pipelineStageMapId
) =>
  hasVideoInterviewInStage(jobData, pipelineStageMapId) ||
  hasAssessmentsInStage(jobData, pipelineStageMapId) ||
  hasLiveInterviewInStage(jobData, pipelineStageMapId);

/** convert a string of words to one word in camel case */
export function camelize(str) {
  return str
    .replace(/(?:^\w|[A-Z]|\b\w)/g, (word, index) =>
      index === 0 ? word.toLowerCase() : word.toUpperCase()
    )
    .replace(/\s+/g, "");
}

export function convertSortArrayToObject(sortArray) {
  const sortData = {};
  sortArray.forEach((sortItem) => {
    sortData[camelize(sortItem)] = parseInt(sortArray.indexOf(sortItem), 10);
  });

  return sortData;
}

const addExtraFilterOptions = (testId, testCustomFilterOptions) => {
  if (!EXTRA_CUSTOM_FILTER_OPTIONS_LIBRARY[testId]) {
    return testCustomFilterOptions;
  }

  return testCustomFilterOptions.map((option, i) => {
    if (EXTRA_CUSTOM_FILTER_OPTIONS_LIBRARY[testId][i]) {
      return {
        ...option,
        ...EXTRA_CUSTOM_FILTER_OPTIONS_LIBRARY[testId][i],
      };
    }
    return option;
  });
};

export const determineCustomFilterOptions = (
  testId,
  testMakerTests,
  fromScoreRangeForm = false
) => {
  const testCustomFilterOptions = CUSTOM_FILTER_OPTIONS_LIBRARY[testId];

  if (testCustomFilterOptions) {
    // some filter options should only be added when this is not called from the score range form
    return fromScoreRangeForm
      ? testCustomFilterOptions
      : addExtraFilterOptions(testId, testCustomFilterOptions);
  }

  const testIdToUse = String(testId).toLowerCase().includes("tm")
    ? testId.substring(2)
    : testId;

  if (
    testMakerTests &&
    testMakerTests.get(testIdToUse) &&
    !testMakerTests.getIn([testIdToUse, "isTyping"])
  ) {
    return [
      {
        testAbbrev: testMakerTests.getIn([testIdToUse, "testMakerTestName"]),
        testType: "Test Maker",
      },
      {
        "By Raw Score": {
          title: "N/A",
          defaultValue: {
            min: 0,
            max: testMakerTests.getIn([testIdToUse, "questionCount"]),
          },
          values: null,
        },
        // Percentile Score Range is not supported for Test Maker tests
      },
      null,
      {
        TM: { first: "By Raw Score", second: null },
        percentile: { first: "By Overall Percentile", second: null },
      },
    ];
  }

  return [
    {
      testAbbrev: testMakerTests.get(testIdToUse)
        ? testMakerTests.getIn([testIdToUse, "testMakerTestName"])
        : "TM Typing",
      testType: "TestMaker Typing",
    },
    {
      "By Adjusted WPM": {
        title: "N/A",
        defaultValue: {
          min: 0,
          max: 160,
        },
        values: null,
      },
    },
    null,
    {
      TM: { first: "By Adjusted WPM", second: null },
    },
  ];
};

export const getChartConfig = (scores, isIllustrait, isWIR) => {
  const labels = isIllustrait
    ? [
        "Reliable",
        "Ambitious",
        "Socially Bold",
        "Innovative",
        "Self-willed",
        "Critical",
        "Reserved",
        "Careful",
      ]
    : [
        "Achievement",
        "Motivation",
        "Conscientiousness",
        "Assertiveness",
        "Extroversion",
        "Cooperativeness",
        "Competitiveness",
        "Patience",
        "Self-Confidence",
        "Openness",
      ];

  // Illustrait charts have 8 data points while regular WIR chart has 10
  const datasets = [
    {
      data: isIllustrait
        ? [25, 25, 25, 25, 25, 25, 25, 25]
        : [25, 25, 25, 25, 25, 25, 25, 25, 25, 25],
      fill: true,
      borderColor: "rgb(183.6, 191.25, 201.45)",
      backgroundColor: "rgba(204, 209, 219, 1)",
      borderWidth: 1,
      pointBorderWidth: 0,
      pointHitRadius: 0,
      pointRadius: 0,
    },
    {
      data: scores,
      fill: true,
      backgroundColor: "rgba(0, 31, 78, 0.2)",
      borderColor: "rgb(0, 31, 78)",
      pointBackgroundColor: "rgb(0, 31, 78)",
      pointBorderColor: "#fff",
      pointHoverBackgroundColor: "#fff",
      pointHoverBorderColor: "rgb(0, 31, 78)",
    },
    {
      data: isIllustrait
        ? [75, 75, 75, 75, 75, 75, 75, 75]
        : [75, 75, 75, 75, 75, 75, 75, 75, 75, 75],
      fill: true,
      backgroundColor: "rgba(255, 255, 255, 0)",
      borderColor: "rgb(100, 100, 100)",
      borderDash: [3, 3],
      borderDashOffset: 3,
      borderWidth: 2,
      pointBorderWidth: 0,
      pointHitRadius: 0,
      pointRadius: 0,
    },
  ];

  const pointLabels = {
    // the Illustrait labels' fontColor needs to be transparent because we will overlay
    // them with new labels via the ChartJsLabelPlugin, and need to avoid visual overlap.
    fontColor: isIllustrait
      ? [
          "rgba(0, 0, 0, 0)",
          "rgba(0, 0, 0, 0)",
          "rgba(0, 0, 0, 0)",
          "rgba(0, 0, 0, 0)",
          "rgba(0, 0, 0, 0)",
          "rgba(0, 0, 0, 0)",
          "rgba(0, 0, 0, 0)",
          "rgba(0, 0, 0, 0)",
        ]
      : [
          "#425CC7",
          "#425CC7",
          "#425CC7",
          "#ffa300",
          "#ffa300",
          "#ffa300",
          "#FF5C39",
          "#FF5C39",
          "#2dccd3",
          "#2dccd3",
        ],
    fontStyle: "bold",
    fontSize: 13,
  };

  const config = {
    type: "radar",
    data: {
      labels,
      datasets,
    },
    options: {
      animation: false,
      legend: {
        display: false,
      },
      scale: {
        ticks: {
          beginAtZero: true,
          min: 0,
          max: 125,
          stepSize: 25,
          display: false,
        },
        pointLabels,
      },
      layout: {
        padding: {
          top: isIllustrait ? 65 : 0,
          right: isIllustrait ? 0 : 25,
          bottom: isIllustrait ? 65 : 0,
          left: isIllustrait ? 0 : 25,
        },
      },
      tooltips: {
        enabled: false,
      },
    },
    plugins: isIllustrait ? [ChartJsLabelPlugin(isWIR)] : "",
  };

  return config;
};

// Function to filter structured interview template options by their interview type, active status and questions
// interviewType: liveInterview or videoInterview
export const getFilteredInterviewTemplates = (
  structuredInterviewTemplates,
  interviewType
) => {
  if (!structuredInterviewTemplates) {
    return new Map();
  }

  // Both types of interviews should only provide active templates with at least one question
  const activeTemplatesWithQuestions = structuredInterviewTemplates.filter(
    (template) =>
      template.get("active") &&
      template
        .get("interviewSteps")
        .filter(
          (interviewStep) =>
            interviewStep.get("interviewStepType") === "question"
        ).size > 0
  );

  return interviewType === "liveInterview"
    ? activeTemplatesWithQuestions.filter((template) => template.get("isLive"))
    : activeTemplatesWithQuestions.filter((template) =>
        template.get("isAsync")
      );
};

// exclude the not-hired stages
export const getFilteredPipelineStages = (currentPipeline) => {
  const filteredPipelineStages = [];

  if (!(currentPipeline && currentPipeline.get("stages"))) {
    return filteredPipelineStages;
  }

  currentPipeline
    .get("stages")
    .valueSeq()
    .sort(
      (stage1, stage2) => stage1.get("stageOrder") - stage2.get("stageOrder")
    )
    .forEach((stage) => {
      if (!stage.get("notHiredStatus") && stage.get("stageName") !== "Hired") {
        filteredPipelineStages.push(stage);
      }
    });

  return filteredPipelineStages;
};

export const getAutomationIcon = (
  showLiveInterviewIcon,
  showVideoInterviewIcon
) => {
  let actionIcon = null;
  if (showLiveInterviewIcon) {
    actionIcon = (
      <img
        src={liveInterviewIcon}
        className="fontAwesome-icon-svg-img-wrapper"
        alt="Live Interview"
      />
    );
  } else {
    actionIcon = (
      <FontAwesome
        name={showVideoInterviewIcon ? "video-camera" : "paper-plane-o"}
      />
    );
  }

  return actionIcon;
};

export const getEndCountIndex = (currentPage, totalItems) => {
  if (currentPage === 1) {
    return totalItems <= 50 ? totalItems : 50;
  }

  return currentPage * 50 < totalItems ? currentPage * 50 : totalItems;
};

// format phone number to United States format
export const formatPhoneNumberUS = (value, countryCode) => {
  // if the value is empty without the country code, return the empty string
  const phoneNumberWithoutCountryCode = value.replace(`${countryCode} `, "");

  if (!phoneNumberWithoutCountryCode) {
    return phoneNumberWithoutCountryCode;
  }

  const phoneNumber = phoneNumberWithoutCountryCode.replace(/[^.\d]/g, "");
  const phoneNumberLength = phoneNumber.length;

  if (phoneNumberLength < 4) {
    return `${countryCode} ${phoneNumber}`;
  }

  if (phoneNumberLength < 7) {
    return `${countryCode} (${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(
      3
    )}`;
  }

  return `${countryCode} (${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(
    3,
    6
  )}-${phoneNumber.slice(6, 10)}`;
};

// Calculates percent change between a current and previous number
export const calcPercentage = (current, prev) => {
  if (current === undefined || prev === undefined || prev < 0.01) {
    return 0;
  }
  return Math.floor((current / prev) * 1000) / 10 - 100;
};

// Adds commas to number for display & returns string
export const thousandSeparator = (number) => {
  if (number !== null && number !== undefined) {
    return number.toLocaleString("en");
  }
};

export const roundTimeToNearestMinute = (timeStr) => {
  if (!timeStr) {
    return "--";
  }
  // Split the time string into hours, minutes, seconds
  const [hours, minutes, seconds] = timeStr.split(/[:.]/).map(Number);

  // Convert everything to minutes
  const totalMinutes = hours * 60 + minutes + seconds / 60;

  if (isNaN(totalMinutes)) {
    return "--";
  }

  // Return the rounded minutes
  const minutesDisplay = Math.round(totalMinutes);
  return `${minutesDisplay} ${minutesDisplay > 1 ? "minutes" : "minute"}`;
};

export const getDefaultEventExpirationDateTime = (
  currentEventExpirationDate,
  timezone
) => {
  const parsedExpirationDate = parseISO(currentEventExpirationDate);

  // set currentEventExpirationDate and time to the equivalent UTC time
  const currentEventExpirationDateWithTimeUtc = utcToZonedTime(
    parsedExpirationDate,
    timezone
  );

  // get the current local date and time to the equivalent UTC time
  const newDateFromTimezone = utcToZonedTime(new Date(), timezone);

  // set the error state for the eventExpirationTime input if the date and time is before the current date and time
  const isTimeBeforeCurrentTime = isBefore(
    currentEventExpirationDateWithTimeUtc,
    newDateFromTimezone
  );

  const defaultEventExpirationTime = isTimeBeforeCurrentTime
    ? new Date()
    : parseISO(currentEventExpirationDate);

  const defaultEventExpirationDate = isTimeBeforeCurrentTime
    ? format(addDays(new Date(), 3), "yyyy-MM-dd")
    : format(parsedExpirationDate, "yyyy-MM-dd");

  return {
    defaultEventExpirationDate,
    defaultEventExpirationTime,
  };
};

/*
 * The date-fns library does not have some of the timezone abbreviations. The best way to make
 * the code maintainable and scalable would be to create custom formatting for the abbreviations.
 * If a new city/timezone is added, we can simply check if there is an abbreviation for it and if not
 * then we apply the same format as below. The main timezone libraries do not have the listed timezones
 * with the correct abbreviation so until then we will need to do this.
 */
export const getCustomTimezoneWithNewAbbreviation = (
  formattedDate,
  timezone
) => {
  if (formattedDate.includes("UTC")) {
    return formattedDate.replace("UTC", "GMT");
  }

  switch (timezone) {
    case "Pacific/Tahiti":
      return formattedDate.replace("GMT-10", "TAHT");
    case "America/Argentina/Buenos_Aires":
      return formattedDate.replace("GMT-3", "ART");
    case "America/Sao_Paulo":
      return formattedDate.replace("GMT-3", "BRT");
    case "Atlantic/Azores":
      if (formattedDate.includes("GMT-1")) {
        return formattedDate.replace("GMT-1", "AZOT");
      }
      return formattedDate.replace("GMT", "AZOST");
    case "Europe/Istanbul":
      return formattedDate.replace("GMT+3", "TRT");
    case "Asia/Dubai":
      return formattedDate.replace("GMT+4", "GST");
    case "Asia/Kabul":
      return formattedDate.replace("GMT+4:30", "AFT");
    case "Asia/Karachi":
      return formattedDate.replace("GMT+5", "PKT");
    case "Asia/Singapore":
      return formattedDate.replace("GMT+8", "SGT");
    case "Indian/Maldives":
      return formattedDate.replace("GMT+5", "MVT");
    case "Asia/Kathmandu":
      return formattedDate.replace("GMT+5:45", "NPT");
    case "Asia/Dhaka":
      return formattedDate.replace("GMT+6", "BST");
    case "Indian/Cocos":
      return formattedDate.replace("GMT+6:30", "CCT");
    case "Asia/Bangkok":
      return formattedDate.replace("GMT+7", "ICT");
    case "Asia/Jakarta":
      return formattedDate.replace("GMT+7", "WIB");
    case "Pacific/Nauru":
      return formattedDate.replace("GMT+12", "NRT");
    case "Australia/Sydney":
      if (formattedDate.includes("GMT+10")) {
        return formattedDate.replace("GMT+10", "AEST");
      }
      return formattedDate.replace("GMT+11", "AEDT");
    case "Pacific/Kiritimati":
      return formattedDate.replace("GMT+14", "LINT");
    case "Pacific/Pago_Pago":
      return formattedDate.replace("GMT-11", "SST");
    case "Europe/Berlin":
    case "Europe/Brussels":
    case "Europe/Madrid":
    case "Europe/Prague":
    case "Europe/Zurich":
    case "Europe/Paris":
      if (formattedDate.includes("GMT+2")) {
        return formattedDate.replace("GMT+2", "CEST");
      }
      return formattedDate.replace("GMT+1", "CET");
    case "Europe/Helsinki":
      if (formattedDate.includes("GMT+3")) {
        return formattedDate.replace("GMT+3", "EEST");
      }
      return formattedDate.replace("GMT+2", "EET");
    case "Asia/Calcutta":
      return formattedDate.replace("GMT+5:30", "IST");
    case "Asia/Hong_Kong":
      return formattedDate.replace("GMT+8", "HKT");
    case "Asia/Tokyo":
      return formattedDate.replace("GMT+9", "JST");
    case "Australia/Darwin":
    case "Australia/Adelaide":
      return formattedDate.replace("GMT+9:30", "ACST");
    case "Australia/Perth":
      return formattedDate.replace("GMT+8", "AWST");
    case "Australia/Brisbane":
      return formattedDate.replace("GMT+10", "AEST");
    case "Pacific/Auckland":
      if (formattedDate.includes("GMT+12")) {
        return formattedDate.replace("GMT+12", "NZST");
      }
      return formattedDate.replace("GMT+13", "NZDT");
    case "Europe/London":
      return formattedDate.replace("GMT+1", "BST");
    case "Africa/Lagos":
      return formattedDate.replace("GMT+1", "WAT");
    case "Asia/Shanghai":
      return formattedDate.replace("GMT+8", "CST");
    case "America/Bogota":
      return formattedDate.replace("GMT-5", "COT");
    case "America/Caracas":
      return formattedDate.replace("GMT-4", "VET");
    case "Asia/Seoul":
      return formattedDate.replace("GMT+9", "KST");
    default:
      return formattedDate;
  }
};

export const getSelectedEmailTemplateName = (
  selectedEmailTemplateId,
  emailTemplates,
  defaultEmailTemplateId,
  defaultEmailTemplateName
) => {
  let selectedEmailTemplate = null;
  if (
    selectedEmailTemplateId !== null &&
    emailTemplates &&
    emailTemplates.size > 0
  ) {
    selectedEmailTemplate = emailTemplates.get(String(selectedEmailTemplateId));
  }

  // if for any reason `selectedEmailTemplateId` is not null and `selectedEmailTemplate` is undefined (can't find a match in emailTemplates)
  // return an empty string for this case early and update the selectedEmailTemplateId to the defaultEmailTemplateId in the parent component
  if (
    selectedEmailTemplate === undefined &&
    String(selectedEmailTemplateId) !== defaultEmailTemplateId
  ) {
    return "";
  }

  const selectedEmailTemplateName =
    String(selectedEmailTemplateId) === defaultEmailTemplateId ||
    selectedEmailTemplate === null
      ? defaultEmailTemplateName
      : selectedEmailTemplate.get("emailName");

  return selectedEmailTemplateName;
};

export const getEmailTemplateDropdownItems = (emailTemplates) => {
  let emailDropdownItems = [];
  if (emailTemplates && emailTemplates.size > 0) {
    emailDropdownItems = emailTemplates
      .filter(
        (template) =>
          (template.get("emailBody").includes("testCenterLink") &&
            template.get("emailBody").includes("eventId")) ||
          template.get("emailBody").includes("candidateTestingLink")
      )
      .valueSeq()
      .sort((a, b) => a.get("emailName").localeCompare(b.get("emailName")))
      .map((template) => (
        <Dropdown.Item
          key={template.get("emailTemplateId")}
          eventKey={template.get("emailTemplateId")}
        >
          {template.get("emailName")}
        </Dropdown.Item>
      ));
  }

  return emailDropdownItems;
};

// format phone number to Australia format
export const formatPhoneNumberAU = (value, countryCode) => {
  // if the value is empty without the country code, return the empty string
  const phoneNumberWithoutCountryCode = value.replace(`${countryCode} `, "");

  if (!phoneNumberWithoutCountryCode) {
    return phoneNumberWithoutCountryCode;
  }

  const phoneNumber = phoneNumberWithoutCountryCode.replace(/[^.\d]/g, "");
  const phoneNumberLength = phoneNumber.length;

  if (phoneNumberLength < 4) {
    return `${countryCode} ${phoneNumber}`;
  }

  if (phoneNumberLength < 7) {
    return `${countryCode} ${phoneNumber.slice(0, 3)}-${phoneNumber.slice(3)}`;
  }

  return `${countryCode} ${phoneNumber.slice(0, 3)}-${phoneNumber.slice(
    3,
    6
  )}-${phoneNumber.slice(6, 9)}`;
};

// format phone number to United Kingdom format
export const formatPhoneNumberUK = (value, countryCode) => {
  // if the value is empty without the country code, return the empty string
  const phoneNumberWithoutCountryCode = value.replace(`${countryCode} `, "");

  if (!phoneNumberWithoutCountryCode) {
    return phoneNumberWithoutCountryCode;
  }

  const phoneNumber = phoneNumberWithoutCountryCode.replace(/[^.\d]/g, "");
  const phoneNumberLength = phoneNumber.length;

  // UK phone number validates to 11 digits - less than 9 digits is invalid but will still allowed to be added
  if (phoneNumberLength > 11) {
    return `${countryCode} ${phoneNumber.slice(0, 11)}`;
  }

  return `${countryCode} ${phoneNumber}`;
};

// format phone number to India format
export const formatPhoneNumberIN = (value, countryCode) => {
  // if the value is empty without the country code, return the empty string
  const phoneNumberWithoutCountryCode = value.replace(`${countryCode} `, "");

  if (!phoneNumberWithoutCountryCode) {
    return phoneNumberWithoutCountryCode;
  }

  const phoneNumber = phoneNumberWithoutCountryCode.replace(/[^.\d]/g, "");
  const phoneNumberLength = phoneNumber.length;

  // India phone number validates to 10 digits
  if (phoneNumberLength > 10) {
    return `${countryCode} ${phoneNumber.slice(0, 10)}`;
  }

  return `${countryCode} ${phoneNumber}`;
};

export const getFormattedPhoneNumber = (
  phoneNumber,
  countryCode,
  countryCodeAbbreviation
) => {
  if (countryCodeAbbreviation === "AU") {
    return formatPhoneNumberAU(phoneNumber, countryCode);
  }
  if (countryCodeAbbreviation === "UK") {
    return formatPhoneNumberUK(phoneNumber, countryCode);
  }
  if (countryCodeAbbreviation === "IN") {
    return formatPhoneNumberIN(phoneNumber, countryCode);
  }

  return formatPhoneNumberUS(phoneNumber, countryCode);
};

export const getCountryCodeDropdownItems = () => (
  <>
    <Dropdown.Item key="US" eventKey="US" className="country-dropdown-items">
      <div>United States (US)</div>
      <div>+1</div>
    </Dropdown.Item>
    <Dropdown.Item key="AU" eventKey="AU" className="country-dropdown-items">
      <div>Australia (AU)</div>
      <div>+61</div>
    </Dropdown.Item>
    <Dropdown.Item key="UK" eventKey="UK" className="country-dropdown-items">
      <div>United Kingdom (UK)</div>
      <div>+44</div>
    </Dropdown.Item>
    <Dropdown.Item key="IN" eventKey="IN" className="country-dropdown-items">
      <div>India (IN)</div>
      <div>+91</div>
    </Dropdown.Item>
  </>
);

export const getAUInstanceCountryCodeDropdownItems = () => (
  <Dropdown.Item key="AU" eventKey="AU" className="country-dropdown-items">
    <div>Australia (AU)</div>
    <div>+61</div>
  </Dropdown.Item>
);

export const getSMSPhoneValidationData = (phoneNumber, phoneCountryCode) => {
  // will need to add library to handle other countries once we add support for them
  // Only works for US, UK, AU, IN
  // default is US
  const phoneNumberRegexUS = /^\+1 \(\d{3}\) \d{3}-\d{4}$/;

  let isValidPhoneNumber = phoneNumberRegexUS.test(phoneNumber);
  let phoneNumberPlaceholder = `${phoneCountryCodes[phoneCountryCode]} (xxx) xxx-xxxx`;
  let phoneNumberRegex = phoneNumberRegexUS;

  switch (phoneCountryCode) {
    case "AU": {
      const phoneNumberRegexAU = /^\+61 \d{3}-\d{3}-\d{3}$/;

      phoneNumberRegex = phoneNumberRegexAU;
      isValidPhoneNumber = phoneNumberRegexAU.test(phoneNumber);
      phoneNumberPlaceholder = `${phoneCountryCodes[phoneCountryCode]} xxx-xxx-xxx`;
      break;
    }
    case "UK": {
      const phoneNumberRegexUK = /^\+44 \d{9,11}$/;

      phoneNumberRegex = phoneNumberRegexUK;
      isValidPhoneNumber = phoneNumberRegexUK.test(phoneNumber);
      phoneNumberPlaceholder = `${phoneCountryCodes[phoneCountryCode]} xxxx xxxxxx`;
      break;
    }
    case "IN": {
      const phoneNumberRegexIN = /^\+91 \d{10}$/;

      phoneNumberRegex = phoneNumberRegexIN;
      isValidPhoneNumber = phoneNumberRegexIN.test(phoneNumber);
      phoneNumberPlaceholder = `${phoneCountryCodes[phoneCountryCode]} xxxxxxxxxx`;
      break;
    }
    default:
      break;
  }

  return {
    isValidPhoneNumber,
    phoneNumberPlaceholder,
    phoneNumberRegex,
  };
};

const WorkStylesTraits = [
  "reliable",
  "ambitious",
  "socially_bold",
  "innovative",
  "self_willed",
  "critical",
  "reserved",
  "careful",
];

export const getIllustraitTestTime = (workStylesOn, selectedCompetencySet) => {
  // Default is 4min if no specific config is available
  if (!workStylesOn && !selectedCompetencySet) {
    return 4;
  }

  const uniqueTraitsArr = [];
  if (workStylesOn) {
    WorkStylesTraits.forEach((trait) => uniqueTraitsArr.push(trait));
  }
  if (selectedCompetencySet) {
    selectedCompetencySet.get("competencies").forEach((competency) =>
      competency.get("traits").forEach((trait) => {
        if (uniqueTraitsArr.indexOf(trait.get("trait")) < 0) {
          uniqueTraitsArr.push(trait.get("trait"));
        }
      })
    );
  }
  return Math.ceil((uniqueTraitsArr.length * 6 * 4.29 + 26) / 60);
};

// takes a test Map and returns estimated time in minutes
// Illustrait & CLPT related config params are optional
export const getTestTime = (
  test,
  workStylesOn = false,
  selectedCompetencySet = null,
  clptSkillModules = null
) => {
  const testId = String(test.get("testId"));
  const subTestId = String(test.get("subTestId"));

  switch (testId) {
    case "30":
    case "50":
      return 10;
    case "48":
      return getIllustraitTestTime(workStylesOn, selectedCompetencySet);
    case "57": {
      // If CLPT Express subtest or no modules available, return default timeAllowed
      if (clptExpressSubTestIds.includes(subTestId) || !clptSkillModules) {
        return test.get("timeAllowed") / 60;
      }
      // Dynamic CLPT time based on modules selected
      return clptSkillModules.length * 7;
    }
    default:
      return test.get("timeAllowed") / 60;
  }
};

export const getHSVersionName = (hsVersion) => {
  switch (hsVersion) {
    case 0:
      return "basic";
    case 1:
      return "standard";
    case 2:
      return "pro";
    case 3:
      return "integrated";
    case 4:
      return "enterprise";
    case 5:
      return "suite";
    case 6:
      return "professionalPlus";
    default:
      return "unknown";
  }
};

export const checkIfUserHasPosthireAccess = (userData) =>
  userData?.tmgAccess && checkIfUserHasDevelopAccess(userData);

export const checkIfUserHasDevelopAccess = (userData) =>
  !!(userData?.developByCriteriaAccess || userData?.hsVersion === 5);

export const getOdaOpenJobLink = (
  jobCode,
  userAccountId = null,
  version = null
) => {
  let odaLink = null;

  switch (version) {
    case 2:
      odaLink = `${odaUrl}o/${jobCode}/landing`;
      break;
    case 1:
    default:
      odaLink = `${odaUrl}link/index/${jobCode}`;
      break;
  }

  if (userAccountId) {
    odaLink += `?u=${userAccountId}`;
  }

  return odaLink;
};

export const getOdaInviteLink = (eventId, version = null) => {
  let odaLink = null;
  switch (version) {
    case 2:
      odaLink = `${odaUrl}e/${eventId}/landing`;
      break;
    case 1:
    default:
      odaLink = `${odaUrl}verify/index/${eventId}`;
      break;
  }

  return odaLink;
};

// Constants for Calendly URLs
const CALENDLY_BASE_URL = "https://calendly.com";
const PLATFORM_DISCUSSION_EVENT = "/criteria-platform-discussion";
const CALENDLY_EVENTS = {
  SMALL_BUSINESS: "/d/cq7j-tyk-nsk/criteria-platform-discussion-sb",
  MID_MARKET: "/d/cntx-ty9-fx2/criteria-platform-discussion-mm",
  ENTERPRISE: "/d/cnz3-5n8-7yb/criteria-platform-discussion-enterprise",
};
const AU_DEFAULT_REP = "criteria-ashley-bramble";

/**
 * Generates appropriate Calendly scheduling URL based on user type and company size
 * @param {boolean} isFreeTrial - Whether user is on free trial
 * @param {Object} rep - Rep information containing CSM and SDR details
 * @param {Object} utmParams - Marketing tracking parameters
 * @param {string} numberOfEmployees - Company size range (e.g. "0-100")
 * @returns {string} Formatted Calendly URL
 */
export const getScheduleDemoSIUrl = (
  isFreeTrial,
  rep,
  numberOfEmployees,
  utmParams = {}
) => {
  // Handle missing or incomplete parameters
  const actualRep = rep || { sdr: { firstName: "sales", lastName: "team" } };

  // Format UTM parameters
  const { content = "", campaign = "", source = "", medium = "" } = utmParams;
  const utmParametersLink = `?utm_content=${content}&utm_campaign=${campaign}&utm_source=${source}&utm_medium=${medium}`;

  // Check region
  const isAU = getSubDomain() === "app-au";

  // Case 1: Non-free trial with assigned AM
  if (!isFreeTrial && actualRep?.am?.firstName && actualRep?.am?.lastName) {
    const amFirstName = actualRep.am.firstName
      .replace(/[']/g, "")
      .toLowerCase();
    const amLastName = actualRep.am.lastName.replace(/[']/g, "").toLowerCase();
    return `${CALENDLY_BASE_URL}/criteria-${amFirstName}-${amLastName}/subscription-add-on-conversation-25-mins${utmParametersLink}`;
  }

  // Format SDR names (or use defaults if missing)
  const firstName = actualRep?.sdr?.firstName
    ? actualRep.sdr.firstName.replace(/[^a-zA-Z]/g, "").toLowerCase()
    : "sales";
  const lastName = actualRep?.sdr?.lastName
    ? actualRep.sdr.lastName.replace(/[^a-zA-Z-]/g, "").toLowerCase()
    : "team";

  // Case 2: Free trial with generic "sales team" rep
  if (isFreeTrial && firstName === "sales" && lastName === "team") {
    // For non-AU with employee count, use size-specific links
    if (!isAU && numberOfEmployees) {
      return getEmployeeSizeBasedUrl(numberOfEmployees, utmParametersLink);
    }

    // For AU or missing employee count
    const repName = isAU ? AU_DEFAULT_REP : `criteria-${firstName}-${lastName}`;
    return `${CALENDLY_BASE_URL}/${repName}${PLATFORM_DISCUSSION_EVENT}${utmParametersLink}`;
  }

  // Case 3: Default case (specific SDR)
  return `${CALENDLY_BASE_URL}/criteria-${firstName}-${lastName}${PLATFORM_DISCUSSION_EVENT}${utmParametersLink}`;
};

/**
 * Helper function to get URL based on company size
 * @param {string} employeeCount - Number of employees string (e.g. "0-100")
 * @param {string} utmParams - Formatted UTM parameter string
 * @returns {string} Appropriate Calendly URL
 */
const getEmployeeSizeBasedUrl = (employeeCount, utmParams) => {
  if (employeeCount?.includes("-")) {
    const [minEmployees, maxEmployees] = employeeCount.split("-").map(Number);

    if (minEmployees >= 0 && maxEmployees <= 250) {
      return `${CALENDLY_BASE_URL}${CALENDLY_EVENTS.SMALL_BUSINESS}${utmParams}`;
    }

    if (minEmployees >= 251 && maxEmployees <= 1000) {
      return `${CALENDLY_BASE_URL}${CALENDLY_EVENTS.MID_MARKET}${utmParams}`;
    }
  }

  // Default to enterprise for any other case
  return `${CALENDLY_BASE_URL}${CALENDLY_EVENTS.ENTERPRISE}${utmParams}`;
};

/**
 * Checks if user signed up on or after the cutoff date (June 10, 2025)
 * @param {Object} companyInfo - Immutable.Map containing company information
 * @returns {boolean} - true if signed up on or after cutoff date, false otherwise
 */
export const isSignedUpOnOrCutoffDate = (companyInfo) => {
  const dateSignedUp = companyInfo && companyInfo.get("dateSignedUp");

  if (!dateSignedUp) return false;

  const signupDate = new Date(dateSignedUp);
  const cutoffDate = new Date("2025-06-10");

  return signupDate >= cutoffDate;
};

export const isScreenReaderCompatible = (testId, subTestId) => {
  return (
    screenReaderCompatibleTests.includes(testId) &&
    !(testId === 59 && ctrseTranslatedSubTestIds.includes(subTestId))
  );
};
